<div class="main-view space-y-2 md:space-y-4">
  <app-page-surface-header titleIcon="billing"
    title="Usage &amp; Charges"
    subtitle="Per-client PD usage and the invoices you generate from it. Send and collect them under Invoices."
  />

  <app-list-module-surface
    [config]="listSurfaceConfig"
    [loading]="loading"
    [errorMessage]="actionErr"
    [rowCount]="selectedTenantId ? transactions.length : 0"
  >
    <ng-container listToolbar>
      <div class="flex flex-wrap items-center justify-start gap-2">
        <div class="relative">
          <select
            id="billing-client-select"
            class="form-select bg-white py-3 px-4 text-white-dark rounded-full border-white-light dark:border-[#191e3a] dark:bg-dark"
            [(ngModel)]="selectedTenantId"
            (ngModelChange)="onTenantChange()"
          >
            <option [ngValue]="null">Select a client…</option>
            @for (c of clients; track c.id) {
            <option [ngValue]="c.id">{{ clientLabel(c) }}</option>
            }
          </select>
        </div>
      </div>
    </ng-container>

    <ng-container toolbarActions>
      <a
        routerLink="/invoices"
        [queryParams]="selectedTenantId ? { client: selectedTenantId } : null"
        class="btn inline-flex shrink-0 items-center gap-2 rounded-full border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-black shadow-none hover:bg-gray-50 dark:border-[#191e3a] dark:bg-[#0e1726] dark:text-white dark:hover:bg-white/5"
      >View invoices →</a>
    </ng-container>

    <div listTable class="space-y-4">
      @if (actionMsg) {
      <p class="flex flex-wrap items-center gap-x-3 rounded-lg bg-success/10 px-3 py-2 text-sm text-success">
        <span>{{ actionMsg }}</span>
        @if (generatedForTenantId) {
        <a routerLink="/invoices" [queryParams]="{ client: generatedForTenantId }" class="font-semibold underline">Open invoice →</a>
        }
      </p>
      }

      @if (!selectedTenantId) {
      <p class="py-6 text-center text-sm text-gray-500 dark:text-gray-400">Select a client to view usage and generate invoices.</p>
      } @else {
      <section class="rounded-xl border border-gray-200/80 bg-gray-50/40 px-4 py-3 dark:border-white/10 dark:bg-white/[0.02]">
        <div class="mb-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
          <h2 class="text-sm font-bold text-black dark:text-white">
            {{ isPrepaidClient ? 'Invoice overage' : 'Generate invoice' }}
          </h2>
          <p class="text-xs text-gray-500 dark:text-gray-400">
            {{ isPrepaidClient ? 'Overage:' : 'Un-invoiced:' }}
            <span class="font-semibold text-black dark:text-white">{{ (summary?.pending_total ?? 0) | number:'1.2-2' }}</span>
            <span class="mx-1 text-gray-300 dark:text-gray-600">·</span>
            {{ summary?.pending_count ?? 0 }} PD(s)
          </p>
        </div>
        <p *ngIf="isPrepaidClient" class="mb-3 text-xs text-gray-500 dark:text-gray-400">
          This client prepays for a plan. Sessions inside the plan quota are already paid for and
          cannot be invoiced — only usage past the quota appears above and is billed here.
        </p>
        <div class="flex flex-wrap items-end gap-3">
          <div class="w-full min-w-[9rem] sm:w-auto sm:min-w-[10.5rem]">
            <label for="billing-period-start" class="mb-1 block text-xs text-gray-500">Period start</label>
            <input id="billing-period-start" type="date" class="form-input w-full rounded-lg py-2" [(ngModel)]="periodStart" />
          </div>
          <div class="w-full min-w-[9rem] sm:w-auto sm:min-w-[10.5rem]">
            <label for="billing-period-end" class="mb-1 block text-xs text-gray-500">Period end</label>
            <input id="billing-period-end" type="date" class="form-input w-full rounded-lg py-2" [(ngModel)]="periodEnd" />
          </div>
          <div class="w-20 shrink-0">
            <label for="billing-gst-percent" class="mb-1 block text-xs text-gray-500">GST %</label>
            <input id="billing-gst-percent" type="number" min="0" max="100" class="form-input w-full rounded-lg py-2" [(ngModel)]="gstPercent" />
          </div>
          <div class="min-w-[8rem] flex-1 sm:max-w-xs">
            <label for="billing-notes" class="mb-1 block text-xs text-gray-500">Notes</label>
            <input id="billing-notes" type="text" class="form-input w-full rounded-lg py-2" [(ngModel)]="notes" placeholder="optional" />
          </div>
          <button
            type="button"
            class="w-full shrink-0 rounded-full bg-[#303030] px-5 py-2 text-sm font-semibold text-white hover:bg-black disabled:opacity-50 sm:w-auto"
            [disabled]="generating || (summary?.pending_count ?? 0) === 0"
            (click)="generate()"
          >
            {{ generating ? 'Generating…' : (isPrepaidClient ? 'Invoice overage' : 'Generate') }}
          </button>
          <button
            type="button"
            class="w-full shrink-0 rounded-full border border-gray-300 px-5 py-2 text-sm font-semibold text-black hover:bg-gray-50 disabled:opacity-50 dark:border-white/20 dark:text-white dark:hover:bg-white/5 sm:w-auto"
            [disabled]="!canPreviewDraft"
            (click)="previewDraft()"
          >
            Preview draft
          </button>
        </div>
      </section>

      <section class="border-t border-gray-200/80 pt-4 dark:border-white/10">
        <h2 class="mb-2 text-sm font-bold text-black dark:text-white">Usage transactions</h2>
        <app-list-module-table variant="surface" [columns]="txnColumns" [rows]="transactions" [loading]="loading" rowTrackKey="id" emptyMessage="No usage charges yet.">
          <ng-template listCellDef="date" let-row="row"><span class="whitespace-nowrap text-xs text-gray-500">{{ row.occurred_at | date:'dd MMM y' }}</span></ng-template>
          <ng-template listCellDef="product" let-row="row">{{ row.product?.name ?? '—' }}</ng-template>
          <ng-template listCellDef="session" let-row="row"><span class="font-mono text-xs text-gray-500">{{ row.pd_session_code ?? '—' }}</span></ng-template>
          <ng-template listCellDef="amount" let-row="row"><span class="whitespace-nowrap">{{ row.currency }} {{ row.amount | number:'1.2-2' }}</span></ng-template>
          <ng-template listCellDef="status" let-row="row"><span class="rounded-full px-2 py-0.5 text-xs font-medium" [ngClass]="statusClass(row.status)">{{ row.status }}</span></ng-template>
        </app-list-module-table>
      </section>
      }
    </div>
  </app-list-module-surface>

  @if (preview) {
  <app-pdf-preview-modal [title]="preview.title" [filename]="preview.filename" [load]="preview.load" (closed)="closePreview()" />
  }
</div>
